import { Dimension, WidgetTypes } from "@superblocksteam/shared";

import { EmptyMargin } from "legacy/components/propertyControls/MarginControl";
import {
  MarginIconPrefix,
  PaddingIconPrefix,
} from "legacy/components/utils/IconUtil";
import {
  PropsCategory,
  type Hidden,
  type PropertyPaneControlConfig,
  type PropertyPaneSectionConfig,
} from "legacy/constants/PropertyControlConstants";
import type {
  HeaderType,
  UpdateHook,
} from "legacy/constants/PropertyControlConstants";
import {
  CanvasDefaults,
  CanvasLayout,
  GridDefaults,
  WidgetLabelPosition,
} from "legacy/constants/WidgetConstants";
import { VALIDATION_TYPES } from "legacy/constants/WidgetValidation";
import type { CanvasWidgetsReduxState } from "legacy/reducers/entityReducers/canvasWidgetsReducer";
import { getWidgets, isColumnUtil } from "legacy/selectors/entitiesSelector";
import { LABEL_POSITION_PROPERTY_CONTROL_HELP_TEXT } from "pages/Editors/AppBuilder/Sidebar/PropertyControlCommons";
import { Flag } from "store/slices/featureFlags";
import type { Flags } from "store/slices/featureFlags";

import { getWidgetDefaultPadding, ZERO_PADDING } from "./base/sizing";
import type { WidgetProps } from "./BaseWidget";
import {
  heightModeSupportsMinMax,
  widthModeSupportsMinMax,
} from "./layoutProperties";
import { isStackLayout } from "./StackLayout/utils";

const hideWidthMode = (
  supportFitContent: boolean,
  hideWidthMode: boolean,
  props: any,
  path: string,
  flags: Flags,
) => {
  if (hideWidthMode) return true;
  return false;
};

export const sizeProperties = (
  props: {
    heightSupportsFitContent?: boolean | ((flags: Flags) => boolean);
    widthSupportsFitContent?: boolean;
    hideWidthMode?: boolean;
    hideMinMaxHeightSection?: boolean;
    hideMinMaxWidthSection?: boolean;
    hideMargin?: boolean;
    hidePadding?: boolean;
  } = {},
) => {
  const hidePadding = "hidePadding" in props ? props.hidePadding : true;

  const options = [
    ...(!hidePadding ? [paddingProperty({})] : []),
    {
      propertyName: "width",
      propertyCategory: PropsCategory.Layout,
      label: "Width",
      controlType: "WIDTH_DROPDOWN",
      isBindProperty: true,
      isTriggerProperty: false,
      hidden: hideWidthMode.bind(
        null,
        props.widthSupportsFitContent ?? false,
        props.hideWidthMode ?? false,
      ),
      supportFitContent: props.widthSupportsFitContent,
    },
    ...(!props.hideMinMaxWidthSection ? minMaxWidthProperties() : []),
    {
      propertyName: "height",
      propertyCategory: PropsCategory.Layout,
      label: "Height",
      controlType: "HEIGHT_DROPDOWN",
      isBindProperty: true,
      isTriggerProperty: false,
      getAdditionalHiddenData: {
        canvasWidgets: getWidgets,
      },
      supportFitContent: props.heightSupportsFitContent,
    },
    ...(!props.hideMinMaxHeightSection ? minMaxHeightProperties() : []),
    ...(!props.hideMargin ? [marginProperty()] : []),
  ];
  return options satisfies PropertyPaneControlConfig<any>[];
};

export const sizeSection = (
  props: {
    heightSupportsFitContent?: boolean | ((flags: Flags) => boolean);
    widthSupportsFitContent?: boolean;
    hideMinMaxHeightSection?: boolean;
    hideMinMaxWidthSection?: boolean;
    hideMargin?: boolean;
    hidePadding?: boolean;
    sectionName?: string;
    showHeader?: boolean;
    headerType?: HeaderType;
  } = {},
): PropertyPaneSectionConfig => ({
  sectionName: props.sectionName || "Size",
  sectionCategory: PropsCategory.Layout,
  showHeader: props.showHeader,
  headerType: props.headerType,
  getAdditionalHiddenData: {
    canvasWidgets: getWidgets,
  },
  hidden: (_props, path, flags, additionalHiddenData) => {
    const isColumn = additionalHiddenData?.canvasWidgets
      ? isColumnUtil(additionalHiddenData.canvasWidgets, _props.widgetId)
      : false;
    if (
      // if it is tab canvas
      _props.type === WidgetTypes.CANVAS_WIDGET &&
      !isColumn
    ) {
      return true;
    }
    return false;
  },
  children: [...sizeProperties(props)],
});

const minMaxHeightProperties = (): PropertyPaneControlConfig[] => [
  {
    propertyName: "minHeight",
    propertyCategory: PropsCategory.Layout,
    label: "Min height",
    hidden: (props: WidgetProps, propertyPath, flags: Flags) => {
      return !heightModeSupportsMinMax(props.height.mode);
    },
    visibility: "SHOW_NAME",
    isRemovable: true,
    defaultValue: Dimension.px(-1),
    controlType: "DIMENSION_VALUE_CONTROL",
    isBindProperty: false,
    isTriggerProperty: false,
    dimensionUnit: "px",
    parentSpace: GridDefaults.DEFAULT_GRID_ROW_HEIGHT,
    minValue: GridDefaults.DEFAULT_GRID_ROW_HEIGHT,
  },
  {
    propertyName: "maxHeight",
    propertyCategory: PropsCategory.Layout,
    label: "Max height",
    hidden: (props: WidgetProps, propertyPath, flags: Flags) => {
      return !heightModeSupportsMinMax(props.height.mode);
    },
    visibility: "SHOW_NAME",
    isRemovable: true,
    defaultValue: Dimension.px(-1),
    controlType: "DIMENSION_VALUE_CONTROL",
    isBindProperty: false,
    isTriggerProperty: false,
    dimensionUnit: "px",
    parentSpace: GridDefaults.DEFAULT_GRID_ROW_HEIGHT,
    minValue: GridDefaults.DEFAULT_GRID_ROW_HEIGHT,
  },
];

const shouldHideMinMaxWidth: PropertyPaneControlConfig["hidden"] = (
  props: WidgetProps,
  propertyPath,
  flags: Flags,
  additional,
) => {
  const canvasWidgets = additional?.canvasWidgets as CanvasWidgetsReduxState;
  const parent = canvasWidgets?.[props.parentId ?? ""];
  const parentIsStack = isStackLayout(parent?.layout);
  return !widthModeSupportsMinMax(props.width.mode) || !parentIsStack;
};

const minMaxWidthGetAdditionalHiddenData = {
  canvasWidgets: getWidgets,
};

const minMaxWidthProperties = (): PropertyPaneControlConfig[] => [
  {
    propertyName: "minWidth",
    propertyCategory: PropsCategory.Layout,
    label: "Min width",
    hidden: shouldHideMinMaxWidth,
    getAdditionalHiddenData: minMaxWidthGetAdditionalHiddenData,
    visibility: "SHOW_NAME",
    isRemovable: true,
    defaultValue: Dimension.px(-1), // these values don't matter: they get overridden to the current width in widget ops
    controlType: "DIMENSION_VALUE_CONTROL",
    isBindProperty: false,
    isTriggerProperty: false,
    dimensionUnit: "px",
    minValue: CanvasDefaults.MIN_GRID_UNIT_WIDTH,
  },
  {
    propertyName: "maxWidth",
    propertyCategory: PropsCategory.Layout,
    label: "Max width",
    hidden: shouldHideMinMaxWidth,
    getAdditionalHiddenData: minMaxWidthGetAdditionalHiddenData,
    visibility: "SHOW_NAME",
    isRemovable: true,
    defaultValue: Dimension.px(-1), // also overridden
    controlType: "DIMENSION_VALUE_CONTROL",
    isBindProperty: false,
    isTriggerProperty: false,
    dimensionUnit: "px",
    minValue: CanvasDefaults.MIN_GRID_UNIT_WIDTH,
  },
];

export const minMaxWidthPageProperties = (): PropertyPaneControlConfig[] => [
  {
    propertyName: "minWidth",
    propertyCategory: PropsCategory.Layout,
    label: "Min width",
    visibility: "SHOW_NAME",
    isRemovable: true,
    defaultValue: Dimension.px(300),
    controlType: "DIMENSION_VALUE_CONTROL",
    isBindProperty: false,
    isTriggerProperty: false,
    dimensionUnit: "px",
  },
  {
    propertyName: "maxWidth",
    propertyCategory: PropsCategory.Layout,
    label: "Max width",
    visibility: "SHOW_NAME",
    isRemovable: true,
    defaultValue: Dimension.px(7680),
    controlType: "DIMENSION_VALUE_CONTROL",
    isBindProperty: false,
    isTriggerProperty: false,
    dimensionUnit: "px",
  },
];

export const visibleProperties = (
  { useJsExpr = true }: { useJsExpr?: boolean }, // All new widgets should use JS expressions, old ones should not
): PropertyPaneControlConfig[] => [
  isVisibleProperty({ useJsExpr }),

  collapseWhenHiddenProperty({}),
];

export const isVisibleProperty = (
  { useJsExpr = true, hidden }: { useJsExpr?: boolean; hidden?: Hidden }, // All new widgets should use JS expressions, old ones should not
): PropertyPaneControlConfig => ({
  propertyName: "isVisible",
  propertyCategory: PropsCategory.Layout,
  label: "Visible",
  helpText: "Whether the component is visible",
  controlType: "SWITCH",
  isJSConvertible: true,
  hidden,
  isBindProperty: true,
  isTriggerProperty: false,
  validation: VALIDATION_TYPES.BOOLEAN,
  customJSControl: useJsExpr ? "INPUT_JS_EXPR" : undefined,
});

export const isDisabledProperty = ({
  useJsExpr = true,
  helpText = "Disables user interaction with this component",
}: {
  useJsExpr?: boolean; // All new widgets should use JS expressions, old ones should not
  helpText?: string;
}): PropertyPaneControlConfig =>
  ({
    propertyName: "isDisabled",
    propertyCategory: PropsCategory.Interaction,
    label: "Disabled",
    helpText,
    controlType: "SWITCH",
    isJSConvertible: true,
    isBindProperty: true,
    isTriggerProperty: false,
    validation: VALIDATION_TYPES.BOOLEAN,
    customJSControl: useJsExpr ? "INPUT_JS_EXPR" : undefined,
  }) satisfies PropertyPaneControlConfig;

export const collapseWhenHiddenProperty = ({
  hidden,
}: {
  hidden?: Hidden;
}): PropertyPaneControlConfig => ({
  propertyName: "collapseWhenHidden",
  propertyCategory: PropsCategory.Layout,
  label: "Collapse when invisible",
  helpText:
    "When enabled, other components shift up to occupy the space left by this component. If disabled, the component is hidden but leaves blank space. Has no effect when viewing in edit mode",
  controlType: "SWITCH",
  getAdditionalHiddenData: {
    canvasWidgets: getWidgets,
  },
  hidden,
  isJSConvertible: false,
  isBindProperty: true,
  isTriggerProperty: false,
  validation: VALIDATION_TYPES.BOOLEAN,
});

export const marginProperty = () =>
  ({
    propertyName: "margin",
    propertyCategory: PropsCategory.Layout,
    label: "Margin",
    controlType: "MARGIN_CONTROL",
    isBindProperty: false,
    isTriggerProperty: false,
    helpText: "The outer spacing of the component",
    visibility: "SHOW_NAME",
    isRemovable: true,
    defaultValue: EmptyMargin,
    labelIconPrefix: MarginIconPrefix,
    hoverable: true,

    hidden: (
      props: WidgetProps,
      _propertyPath: string,
      flags: Flags,
      additionalHiddenData: any,
    ) => {
      if (!flags[Flag.LAYOUTS_ENABLE_MARGIN]) return true;

      const canvasWidgets =
        additionalHiddenData?.canvasWidgets as CanvasWidgetsReduxState;
      const parent = canvasWidgets?.[props.parentId ?? ""];

      if (!parent) return true;
      const parentIsStack =
        parent.layout === CanvasLayout.HSTACK ||
        parent.layout === CanvasLayout.VSTACK;
      return !parentIsStack;
    },
    getAdditionalHiddenData: {
      canvasWidgets: getWidgets,
    },
  }) satisfies PropertyPaneControlConfig;

export const paddingProperty = (
  overrides: Partial<PropertyPaneControlConfig> = {},
) =>
  ({
    propertyName: "padding",
    propertyCategory: PropsCategory.Layout,
    label: "Padding",
    controlType: "PADDING_CONTROL",
    isBindProperty: false,
    isTriggerProperty: false,
    isRemovable: true,
    visibility: "SHOW_NAME",
    labelIconPrefix: PaddingIconPrefix,
    hoverable: true,
    themeValue: ({ theme, props }) => {
      const value = getWidgetDefaultPadding(theme, props);
      const treatAsNull = value === ZERO_PADDING;
      return {
        treatAsNull,
        value: value,
      };
    },
    ...overrides,
  }) satisfies PropertyPaneControlConfig;

export const spacingProperty = (
  overrides: Partial<PropertyPaneControlConfig> = {},
) =>
  ({
    propertyName: "spacing",
    label: "Spacing",
    isRemovable: false,
    controlType: "SPACING_CONTROL",
    dimensionUnit: "px",
    isBindProperty: false,
    isTriggerProperty: false,
    hoverable: true,
    ...overrides,
  }) satisfies PropertyPaneControlConfig;

export const labelPositionProperty = <T>(opts: {
  orientation?: "legacy" | "horizontal";
  defaultValue?: WidgetLabelPosition;
  parentDottedPath?: string;
  hidden?: Hidden;
  label?: string;
  overridePropertyName?: string;
  helpText?: string;
  updateHook?: UpdateHook;
}) => {
  let options = [];
  let defaultValue;
  let propertyName: string;
  switch (opts.orientation) {
    case "legacy":
      // Legacy: keeps the inner `isVertical` boolean prop control.
      // legacy: boolean options displayed as radio button group
      propertyName = opts.overridePropertyName ?? "isVertical";
      defaultValue = true;
      options = [
        {
          value: true,
          label: "Above",
          icon: "VERTICAL_TOP",
        },
        {
          value: false,
          label: "Beside",
          icon: "ICON_LEFT_ALIGN",
        },
      ];
      break;
    case "horizontal":
      propertyName = opts.overridePropertyName ?? "labelPosition";
      defaultValue = opts.defaultValue ?? WidgetLabelPosition.RIGHT;
      options = [
        {
          label: "Left",
          value: WidgetLabelPosition.LEFT,
          icon: "ICON_LEFT_ALIGN",
        },
        {
          label: "Right",
          value: WidgetLabelPosition.RIGHT,
          icon: "ICON_RIGHT_ALIGN",
        },
      ];
      break;
    default: {
      propertyName = opts.overridePropertyName ?? "position";
      defaultValue = opts.defaultValue ?? WidgetLabelPosition.TOP;
      options = [
        {
          label: "Above",
          icon: "VERTICAL_TOP",
          value: WidgetLabelPosition.TOP,
        },
        {
          label: "Beside",
          value: WidgetLabelPosition.LEFT,
          icon: "ICON_LEFT_ALIGN",
        },
      ];
    }
  }

  return {
    propertyName: opts.parentDottedPath
      ? `${opts.parentDottedPath}.${propertyName}`
      : propertyName,
    label: opts.label ?? "Label position",
    helpText: opts.helpText ?? LABEL_POSITION_PROPERTY_CONTROL_HELP_TEXT,
    controlType: "RADIO_BUTTON_GROUP",
    hidden: opts.hidden ?? ((props: { label?: string }) => !props.label),
    options,
    defaultValue,
    isJSConvertible: true,
    isBindProperty: true,
    isTriggerProperty: false,
    propertyCategory: PropsCategory.Appearance,
    updateHook: opts.updateHook,
  } satisfies PropertyPaneControlConfig as unknown as PropertyPaneControlConfig<T>;
};
